
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import GraphicsManager from "slotworks/graphics/graphics-manager";
import Layers from "slotworks/graphics/layers/layers";
import Point from "slotworks/utils/geom/point";
import * as TweenJS from "slotworks/utils/tween";

export default class LinesComponent {

    /**
     * It is common to reuse a line graphic for inverted / mirrors lines (flips on x or y axis)
     * To flip lines, set it's index (0 indexed) on this map and the axis or axes you want to flip
     * e.g. lineFlips.set(2, new Point(1, 0)); // Flips line 2 on the x axis
     * Line sprites only, this is redundant for runtime drawn lines as no flipping would be required
     */
    public static lineFlips: Map<number, Point> = new Map<number, Point>();
    public static DRAW_LINES: boolean = true;
    public static HIGHLIGHT_MASK?: string;

    protected lines: Array<PIXI.Graphics | PIXI.Sprite>;
    protected highlightLines: Array<PIXI.Graphics | PIXI.Sprite>;

    constructor(lineDefinitions: Point[][]) {
        this.draw(lineDefinitions);
    }

    public draw(lineDefinitions: Point[][]) {
        if (this.lines && this.lines.length) {
            for (const line of this.lines) {
                if (line instanceof PIXI.Graphics) {
                    line.clear();
                }

                Layers.Paylines.remove(line);
            }
        }

        this.lines = [];
        this.highlightLines = [];

        for (let lineIndex = 0; lineIndex < lineDefinitions.length; lineIndex++) {
            const lineDefinition = lineDefinitions[lineIndex];

            let line: PIXI.Graphics | PIXI.Sprite;
            let highlightLine: PIXI.Graphics | PIXI.Sprite;

            const lineSprite = Layers.Paylines.getSprite("line_" + (lineIndex + 1));
            let highlightLineSprite = Layers.Paylines.getSprite("highlight_line_" + (lineIndex + 1));

            if (!highlightLineSprite) {
                highlightLineSprite = lineSprite;
            }

            if (lineSprite) {
                // Graphical lines (assetworks)
                line = lineSprite;
                highlightLine = highlightLineSprite;

                if (LinesComponent.lineFlips.has(lineIndex)) {
                    this.flip(lineIndex, line);
                    if (line !== highlightLine) {
                        this.flip(lineIndex, highlightLine);
                    }
                }
            } else {
                // Draw them ourselves
                if (LinesComponent.DRAW_LINES) {
                    line = this.drawLine(lineDefinition, 10, 0xff0000, 0.2);
                    this.drawLine(lineDefinition, 8, 0xff0000, 0.4, line);
                    this.drawLine(lineDefinition, 6, 0xff0000, 0.6, line);
                    this.drawLine(lineDefinition, 2, 0xffffff, 1, line);
                } else {
                    line = new PIXI.Graphics();
                }

                highlightLine = line;
            }

            line.visible = false;
            highlightLine.visible = false;

            this.lines.push(line);
            this.highlightLines.push(highlightLine);

            Layers.Paylines.add(line);
            Layers.Paylines.add(highlightLine);
        }
    }

    public win(id: number, reverse: boolean = false) {
        const line = this.lines[id - 1];

        this.show(line, reverse);
    }

    public highlight(id: number, reverse: boolean = false) {
        if (LinesComponent.HIGHLIGHT_MASK) {
            this.maskHighlight(id);
        }

        const line = this.highlightLines[id - 1];
        this.show(line, reverse);
    }

    public hideAllLines() {
        for (const line of this.lines) {
            line.visible = false;
        }
        for (const line of this.highlightLines) {
            line.visible = false;
        }
    }

    public tint(id: number, color: number) {
        this.lines[id - 1].tint = color;
        this.highlightLines[id - 1].tint = color;
    }

    public filter(id: number, filters: Array<PIXI.Filter<any>>) {
        this.lines[id - 1].filters = this.highlightLines[id - 1].filters = null;
        this.lines[id - 1].filters = this.highlightLines[id - 1].filters = filters;
    }

    private show(line: PIXI.Graphics | PIXI.Sprite, reverse: boolean = false) {
        line.visible = true;

        if (line instanceof AnimatedSprite) {
            line.reverse = reverse;
            line.gotoAndPlay();
        }
    }

    protected flip(id: number, line: PIXI.Sprite) {
        const flip = LinesComponent.lineFlips.get(id);
        if (flip.x) {
            line.scale.x = -line.scale.x;
        }
        if (flip.y) {
            line.scale.y = -line.scale.y;
            line.y += line.height;
        }
    }

    protected drawLine(lineDefinition: Point[], weight: number, color: number, alpha: number, graphics?: PIXI.Graphics): PIXI.Graphics {
        if (!graphics) {
            graphics = new PIXI.Graphics();
        }

        graphics.lineStyle(weight, color, alpha);

        const firstPosition = lineDefinition[0];
        const firstSymbolPosition = Layers.MatrixContent.getPosition("symbol_" + (firstPosition.x + 1) + "_" + (firstPosition.y + 1));
        graphics.moveTo(firstSymbolPosition.x, firstSymbolPosition.y + firstSymbolPosition.height * 0.5);

        for (const point of lineDefinition) {
            const x = point.x + 1;
            const y = point.y + 1;

            const symbolPosition = Layers.MatrixContent.getPosition(`symbol_${x}_${y}`);

            graphics.lineTo(symbolPosition.x + symbolPosition.width * 0.5, symbolPosition.y + symbolPosition.height * 0.5);
        }

        const lastPosition = lineDefinition[lineDefinition.length - 1];
        const lastSymbolPosition = Layers.MatrixContent.getPosition("symbol_" + (lastPosition.x + 1) + "_" + (lastPosition.y + 1));
        graphics.lineTo(lastSymbolPosition.x + lastSymbolPosition.width, lastSymbolPosition.y + lastSymbolPosition.height * 0.5);

        return graphics;
    }

    private maskHighlight(id: number): void {
        const lineSprite = Layers.Paylines.getSprite("line_" + (id));
        lineSprite.visible = true;
        const highlightSprite = Layers.Paylines.getSprite("highlight_line_" + (id));
        const mask = GraphicsManager.createSprite(LinesComponent.HIGHLIGHT_MASK);
        highlightSprite.parent.addChild(mask);
        highlightSprite.mask = mask;

        mask.x = lineSprite.x - (mask.width / 2);
        mask.y = lineSprite.y;

        new TweenJS.Tween(mask)
            .to({ x: lineSprite.x + (mask.width / 2) }, 1000)
            .onComplete(() => {
                highlightSprite.mask = null;
                mask.destroy();
                highlightSprite.visible = false;
            })
            .start();
    }

}
